import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import QuickSearch from '../components/SearchResults/QuickSearch'; 
import ActiveFilters from '../components/SearchResults/ActiveFilters';
import FilterSidebar from '../components/SearchResults/FilterSidebar';
import YachtTypeSelector from '../components/SearchResults/YachtTypeSelector'; 
import SearchResultsList from '../components/SearchResults/SearchResultsList'; 
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

const SearchResults = () => {
  const location = useLocation();
  // Destructure yacht-specific state from location
  const { 
    searchType = 'yacht', // Default to yacht if coming here directly
    cruiseType = '',
    destination = '',
    bookingDate, // Keep as Date object if possible
    bookingTime = '',
    numberOfGuests = '',
    couponCode = ''
  } = location.state || {};

  // Initialize filters based on yacht search parameters
  const [filters, setFilters] = useState({
    searchType: searchType,
    cruiseType: cruiseType || '',
    destination: destination || '',
    bookingDate: bookingDate?.toISOString() || '', // Store as ISO string for consistency?
    bookingTime: bookingTime || (bookingDate ? `${bookingDate.getHours().toString().padStart(2, '0')}:${bookingDate.getMinutes().toString().padStart(2, '0')}` : ''),
    numberOfGuests: numberOfGuests || '',
    couponCode: couponCode || '',
    // Add new yacht-specific filters with defaults
    yachtType: [], // e.g., ['Motor Yacht', 'Sailing Yacht']
    priceRange: [0, 5000], // Example price range for yachts
    lengthRange: [10, 50], // Example length range in meters
    cabins: '', // Example: number of cabins
  });

  // Update filters if location state changes (e.g., new search from form)
  useEffect(() => {
    if (location.state) {
        setFilters(prev => ({
          ...prev, // Keep existing filters like price, length etc.
          searchType: location.state.searchType || 'yacht',
          cruiseType: location.state.cruiseType || '',
          destination: location.state.destination || '',
          bookingDate: location.state.bookingDate?.toISOString() || '',
          bookingTime: location.state.bookingTime || (location.state.bookingDate ? `${location.state.bookingDate.getHours().toString().padStart(2, '0')}:${location.state.bookingDate.getMinutes().toString().padStart(2, '0')}` : ''),
          numberOfGuests: location.state.numberOfGuests || '',
          couponCode: location.state.couponCode || '',
        }));
    }
  }, [location.state]); 

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Update filter removal logic for yachts
  const handleRemoveFilter = (filterType, value) => {
    switch(filterType) {
      case 'bookingDate':
        setFilters(prev => ({ ...prev, bookingDate: '', bookingTime: '' }));
        break;
      case 'cruiseType':
        setFilters(prev => ({ ...prev, cruiseType: '' }));
        break;
      case 'destination':
        setFilters(prev => ({ ...prev, destination: '' }));
        break;
      case 'numberOfGuests':
        setFilters(prev => ({ ...prev, numberOfGuests: '' }));
        break;
      case 'yachtType':
        setFilters(prev => ({
          ...prev,
          yachtType: prev.yachtType.filter(type => type !== value)
        }));
        break;
      case 'cabins':
        setFilters(prev => ({ ...prev, cabins: '' }));
        break;
      case 'priceRange':
        setFilters(prev => ({ ...prev, priceRange: [0, 5000] })); // Reset to default yacht price range
        break;
      case 'lengthRange':
         setFilters(prev => ({ ...prev, lengthRange: [10, 50] })); // Reset to default yacht length range
        break;
      default:
        break;
    }
  };

  // Update reset logic for yacht filters
  const handleRemoveAllFilters = () => {
    setFilters({
      searchType: 'yacht',
      cruiseType: '',
      destination: '',
      bookingDate: '',
      bookingTime: '',
      numberOfGuests: '',
      couponCode: '',
      yachtType: [], 
      priceRange: [0, 5000], 
      lengthRange: [10, 50],
      cabins: '',
    });
  };

  // Update category change handler (now for yacht types)
  const handleYachtTypeChange = (newTypes) => {
    setFilters(prev => ({
      ...prev,
      yachtType: newTypes
    }));
  };

  // Update quick search handler (now for booking date/time)
  const handleQuickSearchChange = (newDateInfo) => {
    setFilters(prev => ({
      ...prev,
      // Correctly assign properties from newDateInfo
      bookingDate: newDateInfo.bookingDate, 
      bookingTime: newDateInfo.bookingTime,
      // Pass guests from QuickSearch as well
      numberOfGuests: newDateInfo.numberOfGuests
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main>
        <div className="container mx-auto px-4 py-8">
          {/* Mobile Filter Toggle Button */}
          <button
            className="lg:hidden w-full bg-white p-4 mb-4 rounded-lg shadow flex items-center justify-between"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            <span className="font-semibold">Filters</span>
            {isFilterOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </button>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Sidebar - Components inside will need updating */}
            <div className={`lg:w-1/3 ${isFilterOpen ? 'block' : 'hidden lg:block'}`}>
              <div className="space-y-6">
                <QuickSearch 
                  initialFilters={filters} 
                  onSearch={handleQuickSearchChange} 
                />
                <ActiveFilters 
                  filters={filters}
                  onRemoveFilter={handleRemoveFilter}
                  onRemoveAll={handleRemoveAllFilters}
                />
                <FilterSidebar 
                  filters={filters}
                  setFilters={setFilters}
                />
              </div>
            </div>
            
            {/* Main Content - Components inside will need updating */}
            <div className="lg:w-2/3">
              {/* Use YachtTypeSelector */}
              <YachtTypeSelector 
                selectedTypes={filters.yachtType} 
                onTypeChange={handleYachtTypeChange} 
              />
              <SearchResultsList filters={filters} /> 
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SearchResults;